import React, { useState, Fragment, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Tabs, Tab, Loader, PrimaryButton, Container } from '@crehana/ui';
import { getSourceEventParams, useAmplitude } from '@crehana/analytics';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';
import Separator from 'Jsx/CrehanaLive/components/Separator';
import Hero from 'Jsx/CrehanaLive/sections/Hero';
import NextLives from 'Jsx/CrehanaLive/sections/NextLives';
import LiveList from 'Jsx/CrehanaLive/sections/LiveList';
import ClassroomCourseList from 'Jsx/CrehanaLive/sections/ClassroomCourseList';
import B2BFooter from 'Jsx/CrehanaLive/sections/B2BFooter';
import useCurrentAndNextLives from 'Jsx/CrehanaLive/hooks/useCurrentAndNextLives';

import type { OrganizationType } from '../../types';

const MainContainer = styled.div`
  background: linear-gradient(
    180deg,
    transparent 0%,
    ${({ theme }) => theme.colors.primary.main} 100%
  );
`;

interface B2BLandingProps {
  organization: OrganizationType;
}

const B2BLanding: React.FC<B2BLandingProps> = ({ organization }) => {
  const { t } = useCrehanaLiveTranslations();
  const [isSticky, setIsSticky] = useState(false);
  const [currentTab, setCurrentTab] =
    useState<'courses' | 'transmissions'>('courses');
  const { featuredLive, nextLives, showRefetch, loading, refetch } =
    useCurrentAndNextLives({
      isB2B: true,
      variables: { slugOrg: organization?.slug },
    });
  const { logEvent } = useAmplitude();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (organization.slug) {
      logEvent({
        event: 'Render Lives B2B',
        data: {
          ...getSourceEventParams(),
          'Organization Slug': organization.slug,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.slug]);

  useEffect(() => {
    const stickyRef = ref.current;

    if (stickyRef) {
      const observer = new IntersectionObserver(
        ([e]) => e && setIsSticky(e.intersectionRatio < 1),
        { threshold: [1] },
      );

      observer.observe(stickyRef);

      // unmount
      return function () {
        observer.unobserve(stickyRef);
      };
    }
  }, [ref]);

  if (loading) {
    return (
      <div className="pt-64" style={{ minHeight: 448 }}>
        <Separator className="bg-gr" />
        <div className="flex justify-center mt-32">
          <Loader white />
        </div>
      </div>
    );
  }

  if (showRefetch) {
    return (
      <div style={{ minHeight: 448 }} className="px-12 text-center pt-72">
        <Separator />
        <div className="flex flex-col items-center mt-32">
          <p className="font-body3 text-white">{t('ERROR_LOAD_B2B_LANDING')}</p>
          <PrimaryButton
            label={t('RETRY_AGAIN_LABEL')}
            className="block mt-16"
            onClick={() => refetch()}
            style={{ maxWidth: 300 }}
            isDark
          />
        </div>
      </div>
    );
  }

  const renderTransmissions = () => {
    return (
      <Fragment>
        <div className="relativez-1">
          <NextLives lives={nextLives} />
        </div>
        <div className="relative mt-56 z-1">
          <Container className="categories-and-lives">
            <LiveList
              isB2B
              customTitle={organization.configuration?.livesTitleTransmission}
            />
          </Container>
        </div>
      </Fragment>
    );
  };

  const renderCourses = () => {
    return (
      <Fragment>
        <div className="relative mt-56 z-1">
          <Container className="categories-and-lives">
            <ClassroomCourseList
              organizationName={organization.name}
              slug={organization.slug}
            />
          </Container>
        </div>
      </Fragment>
    );
  };

  const content = {
    courses: renderCourses(),
    transmissions: renderTransmissions(),
  };

  return (
    <div>
      <Hero live={featuredLive} configuration={organization?.configuration} />
      <MainContainer className="pt-56 pb-80 min-h-280">
        <div ref={ref} />
        <Tabs<'courses' | 'transmissions'>
          className="mb-24 sm:mb-56 lg:mb-64"
          backgroundColor={isSticky ? 'base-dark' : 'transparent'}
          withGridContainer="default"
          isDark
          sticky
          value={currentTab}
          onTabChange={selectedTab => setCurrentTab(selectedTab)}
        >
          <Tab label={t('COURSE')} value="courses" />
          <Tab label={t('TRANSMISSIONS')} value="transmissions" />
        </Tabs>
        {content[currentTab]}
      </MainContainer>
      <B2BFooter />
    </div>
  );
};

export default B2BLanding;
